import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Building2, Plus, Search, Play, AlertCircle, CheckCircle, Layers, Clock, ArrowUpDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import AddCompanyPanel from '@/components/companies/AddCompanyPanel';
import CompanyDetailCard from '@/components/companies/CompanyDetailCard';
import { toast } from 'sonner';
import { getCompanies } from '@/lib/dataService';
import { formatDate, getDeadlineStatus, daysUntil } from '@/lib/formatters';

const ITEMS_PER_PAGE = 20;

const STATUS_FILTERS = [
  { id: 'all', label: 'All', color: '' },
  { id: 'overdue', label: 'Overdue', color: 'bg-red-100 text-red-700' },
  { id: 'due-soon', label: 'Due Soon', color: 'bg-amber-100 text-amber-700' },
  { id: 'on-track', label: 'On Track', color: 'bg-green-100 text-green-700' },
];

const Companies = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [addPanelTab, setAddPanelTab] = useState(null); // null = closed, otherwise the tab to open on
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortField, setSortField] = useState('accountsDue');
  const [sortDir, setSortDir] = useState('asc');

  // Deep links (sidebar "Add Company") open the panel: /companies?add=1 or ?add=bulk
  useEffect(() => {
    const add = searchParams.get('add');
    if (!add) return;
    setAddPanelTab(add === 'bulk' ? 'bulk' : 'default');
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleCompanyAdded = (created) => {
    setAddPanelTab(null);
    if (created?.id) setCompanies((prev) => [created, ...prev]);
  };
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    async function loadCompanies() {
      try {
        const data = await getCompanies();
        setCompanies(data);
        if (data.length > 0) setSelectedCompany(data[0]);
      } catch (err) {
        console.error('Failed to load companies:', err);
      } finally {
        setLoading(false);
      }
    }
    loadCompanies();
  }, []);

  // Status counts
  const statusCounts = useMemo(() => {
    const counts = { all: companies.length, overdue: 0, 'due-soon': 0, 'on-track': 0 };
    companies.forEach(c => {
      const status = getDeadlineStatus(c.accountsDue);
      counts[status]++;
    });
    return counts;
  }, [companies]);

  // Filter + search + sort
  const processedCompanies = useMemo(() => {
    let result = [...companies];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(c =>
        c.name.toLowerCase().includes(term) ||
        c.registrationNumber?.toLowerCase().includes(term)
      );
    }

    if (statusFilter !== 'all') {
      result = result.filter(c => getDeadlineStatus(c.accountsDue) === statusFilter);
    }

    result.sort((a, b) => {
      let aVal = a[sortField] || '';
      let bVal = b[sortField] || '';
      if (sortField === 'accountsDue' || sortField === 'confirmationDue') {
        aVal = aVal ? new Date(aVal).getTime() : Infinity;
        bVal = bVal ? new Date(bVal).getTime() : Infinity;
      } else {
        aVal = aVal.toString().toLowerCase();
        bVal = bVal.toString().toLowerCase();
      }
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [companies, searchTerm, statusFilter, sortField, sortDir]);

  // Pagination
  const totalPages = Math.ceil(processedCompanies.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedCompanies = processedCompanies.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  useEffect(() => { setCurrentPage(1); }, [searchTerm, statusFilter]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const handleSelectAll = (checked) => {
    setSelectedCompanies(checked ? processedCompanies.map(c => c.id) : []);
  };

  const handleSelectCompany = (companyId, checked) => {
    setSelectedCompanies(prev =>
      checked ? [...prev, companyId] : prev.filter(id => id !== companyId)
    );
  };

  const handleBulkJobCreation = () => {
    if (selectedCompanies.length === 0) {
      toast.error('Please select at least one company');
      return;
    }
    navigate('/wizard/start');
  };

  const getDeadlineBadge = (dateStr) => {
    const status = getDeadlineStatus(dateStr);
    const days = daysUntil(dateStr);
    if (status === 'overdue') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">
          <AlertCircle className="w-3 h-3" />
          {days != null ? `${Math.abs(days)}d overdue` : 'Overdue'}
        </span>
      );
    }
    if (status === 'due-soon') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">
          <Clock className="w-3 h-3" />
          {days != null ? `${days}d left` : 'Due soon'}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
        <CheckCircle className="w-3 h-3" />
        On track
      </span>
    );
  };

  const SortableHeader = ({ field, children, className = '' }) => (
    <th
      className={`text-left py-3 px-4 text-sm font-medium text-muted-foreground cursor-pointer select-none hover:text-foreground ${className}`}
      onClick={() => handleSort(field)}
    >
      <div className="flex items-center gap-1">
        {children}
        <ArrowUpDown className={`w-3 h-3 ${sortField === field ? 'text-primary' : 'opacity-40'}`} />
      </div>
    </th>
  );

  if (loading) {
    return (
      <div data-testid="companies-page">
        <div className="page-header"><h1 className="page-title">My Companies</h1></div>
        <div className="flex items-center justify-center py-20">
          <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div data-testid="companies-page">
      <div className="page-header flex items-center justify-between">
        <div>
          <h1 className="page-title">My Companies</h1>
          <p className="page-subtitle">{companies.length} {companies.length === 1 ? 'entity' : 'entities'} managed</p>
        </div>
        <div className="flex gap-3">
          {selectedCompanies.length > 0 && (
            <Button onClick={handleBulkJobCreation} data-testid="bulk-job-btn" className="bg-green-600 hover:bg-green-700">
              <Layers className="w-4 h-4 mr-2" />
              Create Batch Jobs ({selectedCompanies.length})
            </Button>
          )}
          <Button variant="outline" onClick={() => setAddPanelTab('bulk')}>Bulk Upload</Button>
          <Button onClick={() => setAddPanelTab(addPanelTab ? null : 'default')} data-testid="add-company-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Company
          </Button>
        </div>
      </div>

      {addPanelTab && (
        <AddCompanyPanel
          key={addPanelTab}
          className="mb-6"
          initialTab={addPanelTab === 'bulk' ? 'bulk' : undefined}
          onSaved={handleCompanyAdded}
          onCancel={() => setAddPanelTab(null)}
        />
      )}

      {companies.length === 0 ? (
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="text-center py-16" data-testid="empty-state">
            <Building2 className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
            <h3 className="text-lg font-medium mb-2">No Companies Yet</h3>
            <p className="text-sm text-muted-foreground mb-6">Add your first company to get started with iXBRL conversion</p>
            <Button onClick={() => setAddPanelTab('default')}>Add Company</Button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <div className="bg-card border border-border rounded-lg">
              {/* Search + status filter chips */}
              <div className="p-4 border-b border-border space-y-3">
                <div className="flex items-center gap-4">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Search by name or registration number..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                      data-testid="company-search"
                    />
                  </div>
                  {selectedCompanies.length > 0 && (
                    <p className="text-sm text-muted-foreground whitespace-nowrap">
                      {selectedCompanies.length} selected
                    </p>
                  )}
                </div>
                <div className="flex gap-2 flex-wrap">
                  {STATUS_FILTERS.map(f => (
                    <button
                      key={f.id}
                      onClick={() => setStatusFilter(f.id)}
                      className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                        statusFilter === f.id
                          ? 'bg-primary text-primary-foreground'
                          : f.color || 'bg-muted text-muted-foreground hover:bg-muted/80'
                      }`}
                    >
                      {f.label} ({statusCounts[f.id]})
                    </button>
                  ))}
                </div>
              </div>

              {processedCompanies.length === 0 ? (
                <div className="text-center py-12 px-4">
                  <Search className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">No companies match your filters.</p>
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full" data-testid="companies-table">
                      <thead className="sticky top-0 bg-muted z-10">
                        <tr className="border-b border-border">
                          <th className="text-left py-3 px-4 w-12">
                            <Checkbox
                              checked={selectedCompanies.length === processedCompanies.length && processedCompanies.length > 0}
                              onCheckedChange={handleSelectAll}
                              data-testid="select-all-checkbox"
                            />
                          </th>
                          <SortableHeader field="name">Company</SortableHeader>
                          <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Reg. No.</th>
                          <SortableHeader field="accountsDue">Accounts Due</SortableHeader>
                          <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
                          <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedCompanies.map((company) => (
                          <tr
                            key={company.id}
                            className={`table-row border-b border-border cursor-pointer hover:bg-muted/50 ${selectedCompany?.id === company.id ? 'bg-muted/30' : ''}`}
                            data-testid="company-row"
                            onClick={() => setSelectedCompany(company)}
                          >
                            <td className="py-3 px-4">
                              <Checkbox
                                checked={selectedCompanies.includes(company.id)}
                                onCheckedChange={(checked) => handleSelectCompany(company.id, checked)}
                                onClick={(e) => e.stopPropagation()}
                              />
                            </td>
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-3">
                                <div className="w-9 h-9 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                                  <Building2 className="w-4 h-4" />
                                </div>
                                <div className="min-w-0">
                                  <p className="font-medium text-sm truncate">{company.name}</p>
                                  <p className="text-xs text-muted-foreground">{company.companyType}</p>
                                </div>
                              </div>
                            </td>
                            <td className="py-3 px-4 text-sm font-mono text-muted-foreground">{company.registrationNumber}</td>
                            <td className="py-3 px-4 text-sm">{formatDate(company.accountsDue)}</td>
                            <td className="py-3 px-4">{getDeadlineBadge(company.accountsDue)}</td>
                            <td className="py-3 px-4 text-right">
                              <Button
                                size="sm"
                                onClick={(e) => { e.stopPropagation(); navigate('/wizard/start'); }}
                                data-testid="start-job-btn"
                              >
                                <Play className="w-3 h-3 mr-1" />
                                Start Job
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {totalPages > 1 && (
                    <div className="p-3 border-t border-border flex items-center justify-between bg-muted/30">
                      <div className="text-sm text-muted-foreground">
                        Showing {startIndex + 1}-{Math.min(startIndex + ITEMS_PER_PAGE, processedCompanies.length)} of {processedCompanies.length}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>
                          <ChevronLeft className="w-4 h-4" />
                        </Button>
                        <span className="text-sm px-2">Page {currentPage} of {totalPages}</span>
                        <Button variant="outline" size="sm" disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}>
                          <ChevronRight className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          <div className="lg:col-span-1">
            <CompanyDetailCard company={selectedCompany} onCompanySelect={setSelectedCompany} />
          </div>
        </div>
      )}
    </div>
  );
};

export default Companies;
